import {
  User,
  Building,
  CreditCard,
  Users,
  BarChart3,
  Settings,
} from "lucide-react";
import { useTranslation } from "../../../i18n";

export default function AccountTypes() {
  const { t } = useTranslation();
  const accountTypes = [
    {
      type: t('routes.identity.accounts.types.0.type'),
      icon: <User className="h-8 w-8" />,
      holder: t('routes.identity.accounts.types.0.holder'),
      color: "from-emerald-500 to-teal-600",
      highlights: [
        t('routes.identity.accounts.types.0.highlights.0'),
        t('routes.identity.accounts.types.0.highlights.1'),
        t('routes.identity.accounts.types.0.highlights.2'),
      ],
    },
    {
      type: t('routes.identity.accounts.types.1.type'),
      icon: <Building className="h-8 w-8" />,
      holder: t('routes.identity.accounts.types.1.holder'),
      color: "from-blue-500 to-indigo-600",
      highlights: [
        t('routes.identity.accounts.types.1.highlights.0'),
        t('routes.identity.accounts.types.1.highlights.1'),
        t('routes.identity.accounts.types.1.highlights.2'),
        t('routes.identity.accounts.types.1.highlights.3'),
      ],
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('routes.identity.accounts.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('routes.identity.accounts.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {accountTypes.map((account, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div
                className={`h-2 w-full bg-gradient-to-r ${account.color}`}
              ></div>
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div
                    className={`h-16 w-16 rounded-xl bg-gradient-to-br ${account.color} flex items-center justify-center mr-4 text-white`}
                  >
                    {account.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {account.type}
                    </h3>
                    <p className="text-gray-600">{account.holder}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {t('routes.identity.accounts.highlights')}
                  </h4>
                  {account.highlights.map((highlight, highlightIndex) => (
                    <div key={highlightIndex} className="flex items-start">
                      <div
                        className={`h-2 w-2 rounded-full bg-gradient-to-r ${account.color} mt-2 mr-3 flex-shrink-0`}
                      ></div>
                      <p className="text-gray-600">{highlight}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Management Account Features Visualization */}
        <div className="mt-16 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-200 p-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t('routes.identity.accounts.featuresTitle')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('routes.identity.accounts.features.0.title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('routes.identity.accounts.features.0.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('routes.identity.accounts.features.1.title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('routes.identity.accounts.features.1.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('routes.identity.accounts.features.2.title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('routes.identity.accounts.features.2.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg flex items-center justify-center mx-auto mb-4">
                <Settings className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('routes.identity.accounts.features.3.title')}
              </h4>
              <p className="text-sm text-gray-600">
                {t('routes.identity.accounts.features.3.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
